import { z } from "zod";
import {
  calendarDateSchema,
  paginationQuerySchema,
  timeRangeSchema,
  uuidSchema,
} from "@shared/schemas/common";

export const clinicClosureSchema = z.object({
  id: uuidSchema,
  clinicId: uuidSchema,
  startsOn: calendarDateSchema,
  endsOn: calendarDateSchema,
  reason: z.string(),
  isAnnual: z.boolean(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type ClinicClosure = z.infer<typeof clinicClosureSchema>;

const closureWritableFields = {
  startsOn: calendarDateSchema,
  endsOn: calendarDateSchema,
  reason: z.string().trim().min(2).max(200),
  isAnnual: z.boolean(),
};

const orderedDays = <T extends { startsOn?: string | undefined; endsOn?: string | undefined }>(
  input: T,
): boolean =>
  input.startsOn === undefined || input.endsOn === undefined || input.startsOn <= input.endsOn;

export const createClinicClosureSchema = z
  .object({
    ...closureWritableFields,
    isAnnual: z.boolean().default(false),
  })
  .refine(orderedDays, { message: "endsOn must not be before startsOn", path: ["endsOn"] });
export type CreateClinicClosureInput = z.infer<typeof createClinicClosureSchema>;

export const updateClinicClosureSchema = z
  .object(closureWritableFields)
  .partial()
  .refine((input) => Object.keys(input).length > 0, "At least one field must be provided")
  .refine(orderedDays, { message: "endsOn must not be before startsOn", path: ["endsOn"] });
export type UpdateClinicClosureInput = z.infer<typeof updateClinicClosureSchema>;

export const listClinicClosuresQuerySchema = paginationQuerySchema.extend({
  from: calendarDateSchema.optional(),
  to: calendarDateSchema.optional(),
});
export type ListClinicClosuresQuery = z.infer<typeof listClinicClosuresQuerySchema>;

export const doctorTimeOffSchema = z.object({
  id: uuidSchema,
  clinicId: uuidSchema,
  doctorId: uuidSchema,
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime(),
  reason: z.string(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type DoctorTimeOff = z.infer<typeof doctorTimeOffSchema>;

const timeOffWritableFields = {
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime(),
  reason: z.string().trim().min(2).max(200),
};

const orderedInstants = <T extends { startsAt?: string | undefined; endsAt?: string | undefined }>(
  input: T,
): boolean =>
  input.startsAt === undefined || input.endsAt === undefined || input.startsAt < input.endsAt;

export const createDoctorTimeOffSchema = z
  .object(timeOffWritableFields)
  .refine(orderedInstants, { message: "endsAt must be after startsAt", path: ["endsAt"] });
export type CreateDoctorTimeOffInput = z.infer<typeof createDoctorTimeOffSchema>;

export const updateDoctorTimeOffSchema = z
  .object(timeOffWritableFields)
  .partial()
  .refine((input) => Object.keys(input).length > 0, "At least one field must be provided")
  .refine(orderedInstants, { message: "endsAt must be after startsAt", path: ["endsAt"] });
export type UpdateDoctorTimeOffInput = z.infer<typeof updateDoctorTimeOffSchema>;

export const listDoctorTimeOffQuerySchema = paginationQuerySchema.extend({
  from: z.iso.datetime().optional(),
  to: z.iso.datetime().optional(),
});
export type ListDoctorTimeOffQuery = z.infer<typeof listDoctorTimeOffQuerySchema>;

export const scheduleConflictOptionsSchema = z.object({
  force: z.stringbool().default(false),
  cancelAppointments: z.stringbool().default(false),
});
export type ScheduleConflictOptions = z.infer<typeof scheduleConflictOptionsSchema>;

export const conflictingAppointmentSchema = z.object({
  id: uuidSchema,
  startsAt: z.iso.datetime(),
  durationMinutes: z.number().int().positive(),
  patientName: z.string(),
  patientPhone: z.string().nullable(),
  doctorId: uuidSchema,
});
export type ConflictingAppointment = z.infer<typeof conflictingAppointmentSchema>;

export const SCHEDULE_CONFLICT_ERROR = "schedule_conflict";

export const scheduleConflictSchema = z.object({
  statusCode: z.literal(409),
  error: z.literal(SCHEDULE_CONFLICT_ERROR),
  message: z.string(),
  appointments: z.array(conflictingAppointmentSchema),
});
export type ScheduleConflict = z.infer<typeof scheduleConflictSchema>;

export const closureResultSchema = <TItem extends z.ZodTypeAny>(item: TItem) =>
  z.object({
    item,
    cancelledAppointments: z.number().int().min(0),
  });

export const clinicClosureResultSchema = closureResultSchema(clinicClosureSchema);
export type ClinicClosureResult = z.infer<typeof clinicClosureResultSchema>;

export const doctorTimeOffResultSchema = closureResultSchema(doctorTimeOffSchema);
export type DoctorTimeOffResult = z.infer<typeof doctorTimeOffResultSchema>;

export const CLOSURE_CANCELLATION_PREFIX = "closure:";
export const TIME_OFF_CANCELLATION_PREFIX = "time_off:";

export const closureCancellationReason = (closureId: string): string =>
  `${CLOSURE_CANCELLATION_PREFIX}${closureId}`;

export const timeOffCancellationReason = (timeOffId: string): string =>
  `${TIME_OFF_CANCELLATION_PREFIX}${timeOffId}`;

export const doctorExtraHoursSchema = z.object({
  id: uuidSchema,
  clinicId: uuidSchema,
  doctorId: uuidSchema,
  date: calendarDateSchema,
  ranges: z.array(timeRangeSchema),
  reason: z.string(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type DoctorExtraHours = z.infer<typeof doctorExtraHoursSchema>;

export const createDoctorExtraHoursSchema = z.object({
  date: calendarDateSchema,
  ranges: z.array(timeRangeSchema).min(1).max(6),
  reason: z.string().trim().min(2).max(200),
});
export type CreateDoctorExtraHoursInput = z.infer<typeof createDoctorExtraHoursSchema>;

export const listDoctorExtraHoursQuerySchema = paginationQuerySchema.extend({
  from: calendarDateSchema.optional(),
  to: calendarDateSchema.optional(),
});
export type ListDoctorExtraHoursQuery = z.infer<typeof listDoctorExtraHoursQuerySchema>;
